import { LitElement, html, css, type TemplateResult, type PropertyValues } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { getFirebaseAuth, isDevBypass } from '../lib/firebase';
import { navigate } from '../lib/router-state';
import { setPageTitle } from '../lib/page-title';
import { startInkCanvas } from '../lib/ink-canvas';

/**
 * Fully custom, deck-system-styled sign-in page — firewall-safe edition.
 *
 * The old implementation drove Clerk CLIENT-SIDE (clerk-js), which is
 * blocked by Ricoh's firewall. This version is a THIN flow over the Go
 * backend's OPEN auth endpoints (same-origin, no auth header):
 *
 *   1. email-password — POST { email, password }. The server replies with
 *      either `{ needs2fa: true }` (account has 2FA → ask for a code) or
 *      `{ customToken }` (success).
 *   2. two-factor     — POST { email, password, code }. On `{ customToken }`
 *      we call Firebase `signInWithCustomToken(auth, customToken)` to mint a
 *      real Firebase session, then full-reload to the return-to destination
 *      so the shell gate re-evaluates to 'authed'.
 *
 * Two self-service flows hang off the sign-in step over the OPEN signup/reset
 * endpoints (same shape — same-origin, no Authorization header):
 *
 *   • Sign up — POST /api/auth/signup/start { email } emails a 6-digit code;
 *     POST /api/auth/signup/complete { email, password, code, name? } returns
 *     a `{ customToken }` that we exchange + redirect exactly like sign-in.
 *   • Reset  — POST /api/auth/reset/request { email } ALWAYS 200s (the code is
 *     emailed only if the account exists — anti-enumeration); POST
 *     /api/auth/reset/confirm { email, code, password } updates the password.
 *     Reset does NOT auto-sign-in: on success we return to the sign-in step.
 *
 * Server errors arrive as a non-200 JSON `{ error }`; we surface that
 * message verbatim in the aria-live error region.
 *
 * In dev-bypass mode (localhost, no Firebase config) it shows the existing
 * dev banner and never touches the network.
 */

/** Shape of the OPEN /api/auth/signin response (200). */
interface SignInResponse {
  needs2fa?: boolean;
  customToken?: string;
}

/** Shape of the OPEN /api/auth/signup/complete response (200). */
interface SignupCompleteResponse {
  customToken?: string;
}

type Step =
  | 'email-password'
  | 'two-factor'
  | 'signup-email'
  | 'signup-complete'
  | 'reset-email'
  | 'reset-confirm'
  | 'done';

@customElement('login-page')
export class LoginPage extends LitElement {
  static styles = css`
    :host {
      display: flex;
      min-height: 100vh;
      align-items: center;
      justify-content: center;
      background: var(--canvas);
      padding: 32px;
      position: relative;
      overflow: hidden;
    }
    canvas.ink {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      z-index: 0;
      pointer-events: none;
    }
    .panel {
      position: relative;
      z-index: 1;
      background: var(--surface);
      border: 1px solid var(--border-subtle);
      border-radius: var(--radius, 12px);
      padding: 40px;
      max-width: 420px;
      width: 100%;
      box-shadow: var(--shadow-overlay);
    }
    .eyebrow {
      font-family: var(--font-sans), 'Inter', system-ui, sans-serif;
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--text-tertiary);
      margin: 0 0 8px;
    }
    h1 {
      font-family: var(--font-editorial), 'Fraunces', Georgia, serif;
      font-weight: 400;
      font-size: 1.9rem;
      letter-spacing: -0.01em;
      margin: 0 0 8px;
      color: var(--text-primary);
    }
    .subtitle {
      color: var(--text-secondary);
      margin: 0 0 28px;
      font-size: 0.95rem;
      line-height: 1.5;
    }
    form {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }
    .field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    label {
      font-size: 0.78rem;
      font-weight: 600;
      letter-spacing: 0.01em;
      color: var(--text-secondary);
    }
    input {
      width: 100%;
      font: inherit;
      font-size: 0.95rem;
      color: var(--text-primary);
      background: var(--canvas);
      border: 1px solid var(--border-default, var(--border-subtle));
      border-radius: var(--radius-sm, 8px);
      padding: 11px 13px;
      transition: border-color 120ms, box-shadow 120ms;
    }
    input::placeholder {
      color: var(--text-tertiary);
    }
    input:focus {
      outline: none;
      border-color: var(--ink);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--ink) 18%, transparent);
    }
    input:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    input.code {
      letter-spacing: 0.4em;
      text-align: center;
      font-variant-numeric: tabular-nums;
    }
    .ident {
      font-weight: 600;
      color: var(--text-primary);
    }
    button.primary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 44px;
      padding: 0 18px;
      /* The primary CTA is the BRAND ink — the accent the theme-boot writes to
         --brand-color (RDx green by default; the per-user ink picker overrides
         it). NOT the Paper --ink token: that is a FOREGROUND token that inverts
         to a pale blue-grey on dark themes, which made "Sign in" look disabled.
         --brand-on-accent is the WCAG-correct on-colour the boot computes. */
      background: var(--brand-primary, var(--ink));
      color: var(--brand-on-accent, var(--on-ink));
      border: 1px solid var(--brand-primary, var(--ink));
      border-radius: var(--radius-sm, 8px);
      font-size: 0.95rem;
      font-weight: 600;
      cursor: pointer;
      transition: opacity 120ms, background 120ms, box-shadow 160ms, transform 120ms;
    }
    button.primary:hover:not(:disabled) {
      background: var(--brand-primary-hover, var(--brand-primary, var(--ink-hover, var(--ink))));
      transform: translateY(-1px);
      box-shadow: 0 6px 18px -6px color-mix(in srgb, var(--brand-primary, var(--ink)) 55%, transparent);
    }
    button.primary:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: none;
    }
    button.primary:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-primary, var(--ink)) 35%, transparent);
    }
    button.primary:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
    .links {
      display: flex;
      flex-wrap: wrap;
      gap: 14px;
      margin-top: 4px;
    }
    button.link {
      background: none;
      border: 0;
      padding: 0;
      font: inherit;
      font-size: 0.85rem;
      color: var(--text-secondary);
      cursor: pointer;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    button.link:hover:not(:disabled) {
      color: var(--text-primary);
    }
    button.link:focus-visible {
      outline: none;
      color: var(--text-primary);
      text-decoration-thickness: 2px;
    }
    button.link:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .error {
      background: var(--error-soft);
      border: 1px solid var(--error-strong);
      color: var(--text-primary);
      padding: 12px 14px;
      border-radius: var(--radius-sm, 8px);
      font-size: 0.88rem;
      line-height: 1.45;
      margin: 0;
    }
    .notice {
      color: var(--text-secondary);
      font-size: 0.95rem;
      line-height: 1.6;
      margin: 0;
    }
    .dev-banner {
      background: var(--warning-soft, var(--surface-hover));
      border: 1px solid var(--warning, var(--border-default));
      color: var(--text-primary);
      padding: 14px 16px;
      border-radius: var(--radius-sm, 8px);
      margin-bottom: 18px;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.82rem;
      line-height: 1.5;
    }
  `;

  /** Which step of the thin flow is showing. */
  @state() private step: Step = 'email-password';

  /** True while an auth request is in flight; disables inputs/buttons. */
  @state() private busy = false;

  /** User-facing error message, shown in the aria-live region. */
  @state() private error: string | null = null;

  /**
   * Neutral, non-error status note shown above a step (e.g. the reset
   * anti-enumeration message, or "password updated"). Kept separate from
   * `error` so it renders in the calm `.notice` style, not the red `.error`.
   */
  @state() private notice: string | null = null;

  /** True when a 409 told us the account already exists (offer "Sign in instead"). */
  @state() private accountExists = false;

  /** The email the user entered (carried across steps for the resend/complete POSTs). */
  @state() private email = '';

  /** The password (carried into 2FA / used for signup + reset). */
  @state() private password = '';

  /** The 6-digit code (two-factor / signup / reset steps). */
  @state() private code = '';

  /** Optional display name (signup only). */
  @state() private name = '';

  /** Autofocus target for the current step's first input. */
  @query('[data-autofocus]') private autofocusEl?: HTMLInputElement;

  /** Tracks the step shown on the previous render so we only refocus on change. */
  private lastFocusedStep: Step | null = null;

  /** The flowing-ink background canvas + its teardown handle. */
  @query('canvas.ink') private inkCanvas?: HTMLCanvasElement;
  private stopInk?: () => void;

  connectedCallback(): void {
    super.connectedCallback();
    setPageTitle('Sign in');
  }

  firstUpdated(): void {
    if (this.inkCanvas) this.stopInk = startInkCanvas(this.inkCanvas, this);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopInk?.();
    this.stopInk = undefined;
  }

  protected updated(_changed: PropertyValues): void {
    // Move focus to the active step's first input whenever the step changes.
    if (this.step !== this.lastFocusedStep) {
      this.lastFocusedStep = this.step;
      // Defer so the new input is in the DOM before we focus it.
      queueMicrotask(() => this.autofocusEl?.focus());
    }
  }

  // ─── Generic OPEN auth POST ──────────────────────────────────────────
  // POST a JSON body to one of the OPEN /api/auth/* endpoints. Same-origin,
  // no Authorization header. Returns the parsed 200 body, or throws an Error
  // whose message is the server's `{ error }` string (already user-facing)
  // for non-200 responses. `status` is exposed on the thrown error so callers
  // can branch on it (e.g. 409 "account exists").
  private async postAuth<T>(path: string, body: Record<string, unknown>): Promise<T> {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    let payload: unknown;
    try {
      payload = await res.json();
    } catch {
      payload = null;
    }

    if (!res.ok) {
      const message =
        (payload as { error?: string } | null)?.error ||
        'Something went wrong. Please try again.';
      const err = new Error(message) as Error & { status?: number };
      err.status = res.status;
      throw err;
    }
    return (payload ?? {}) as T;
  }

  // ─── Sign-in request ─────────────────────────────────────────────────
  // POST the credentials (plus the 2FA code if we're on that step) to the
  // OPEN /api/auth/signin endpoint.
  private async postSignin(includeCode: boolean): Promise<SignInResponse> {
    const body: { email: string; password: string; code?: string } = {
      email: this.email.trim(),
      password: this.password,
    };
    if (includeCode) body.code = this.code.trim();
    return this.postAuth<SignInResponse>('/api/auth/signin', body);
  }

  // ─── Exchange the custom token + redirect ────────────────────────────
  // On a successful sign-in (or signup completion) the server returns a
  // Firebase custom token. We exchange it for a real Firebase session via
  // signInWithCustomToken, then do a FULL reload to the return-to
  // destination so the shell gate re-evaluates to 'authed'.
  private async activateAndRedirect(customToken: string): Promise<void> {
    const auth = await getFirebaseAuth();
    if (!auth) {
      this.error = 'Sign-in is unavailable right now. Please reload and try again.';
      this.busy = false;
      return;
    }
    const { signInWithCustomToken } = await import('firebase/auth');
    await signInWithCustomToken(auth, customToken);
    this.step = 'done';
    const dest = sessionStorage.getItem('deck:return-to') || '/';
    sessionStorage.removeItem('deck:return-to');
    // Full reload — the shell's auth gate re-runs and resolves to 'authed'.
    window.location.assign(dest);
  }

  // Route a 200 sign-in response: a custom token completes sign-in; needs2fa
  // drives the second step.
  private async handleResponse(r: SignInResponse): Promise<void> {
    if (r.customToken) {
      await this.activateAndRedirect(r.customToken);
      return;
    }
    if (r.needs2fa) {
      this.code = '';
      this.step = 'two-factor';
      return;
    }
    this.error = 'Sign-in could not be completed. Please try again.';
  }

  // ─── Email + password step submit ────────────────────────────────────
  private onSubmitEmailPassword = async (ev: Event): Promise<void> => {
    ev.preventDefault();
    if (!this.email.trim() || !this.password || this.busy) return;
    this.busy = true;
    this.error = null;
    try {
      const r = await this.postSignin(false);
      await this.handleResponse(r);
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
    } finally {
      this.busy = false;
    }
  };

  // ─── Two-factor step submit ──────────────────────────────────────────
  private onSubmitCode = async (ev: Event): Promise<void> => {
    ev.preventDefault();
    if (!this.code.trim() || this.busy) return;
    this.busy = true;
    this.error = null;
    try {
      const r = await this.postSignin(true);
      await this.handleResponse(r);
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
    } finally {
      this.busy = false;
    }
  };

  // ─── Signup: step A — request a code ─────────────────────────────────
  private onSubmitSignupEmail = async (ev: Event): Promise<void> => {
    ev.preventDefault();
    if (!this.email.trim() || this.busy) return;
    this.busy = true;
    this.error = null;
    this.accountExists = false;
    try {
      await this.postAuth('/api/auth/signup/start', { email: this.email.trim() });
      // Code emailed — advance to the complete step.
      this.code = '';
      this.password = '';
      this.notice = null;
      this.step = 'signup-complete';
    } catch (err) {
      const status = (err as { status?: number }).status;
      // 409: the account already exists — surface the message and offer a
      // one-tap jump to the sign-in step.
      if (status === 409) this.accountExists = true;
      this.error = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
    } finally {
      this.busy = false;
    }
  };

  // ─── Signup: step B — complete with code + password ──────────────────
  private onSubmitSignupComplete = async (ev: Event): Promise<void> => {
    ev.preventDefault();
    if (!this.code.trim() || !this.password || this.busy) return;
    this.busy = true;
    this.error = null;
    try {
      const body: { email: string; password: string; code: string; name?: string } = {
        email: this.email.trim(),
        password: this.password,
        code: this.code.trim(),
      };
      const trimmedName = this.name.trim();
      if (trimmedName) body.name = trimmedName;
      const r = await this.postAuth<SignupCompleteResponse>('/api/auth/signup/complete', body);
      if (r.customToken) {
        await this.activateAndRedirect(r.customToken);
        return;
      }
      this.error = 'Account could not be created. Please try again.';
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
    } finally {
      this.busy = false;
    }
  };

  // ─── Reset: step A — request a code (anti-enumeration: ALWAYS advance) ─
  private onSubmitResetEmail = async (ev: Event): Promise<void> => {
    ev.preventDefault();
    if (!this.email.trim() || this.busy) return;
    this.busy = true;
    this.error = null;
    try {
      // The endpoint always 200s; the code is emailed only if the account
      // exists. We advance regardless and show a neutral notice so a caller
      // cannot tell whether the address is registered.
      await this.postAuth('/api/auth/reset/request', { email: this.email.trim() });
    } catch (err) {
      // A genuine transport/400 error still shows; enumeration-safe 200s never
      // reach here.
      this.error = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      this.busy = false;
      return;
    }
    this.code = '';
    this.password = '';
    this.notice = "If that account exists, we've emailed a code.";
    this.step = 'reset-confirm';
    this.busy = false;
  };

  // ─── Reset: step B — confirm with code + new password (no auto sign-in) ─
  private onSubmitResetConfirm = async (ev: Event): Promise<void> => {
    ev.preventDefault();
    if (!this.code.trim() || !this.password || this.busy) return;
    this.busy = true;
    this.error = null;
    try {
      await this.postAuth('/api/auth/reset/confirm', {
        email: this.email.trim(),
        code: this.code.trim(),
        password: this.password,
      });
      // Success: do NOT auto-sign-in. Wipe the new password from memory,
      // surface a confirmation, and drop the user back on the sign-in step.
      this.password = '';
      this.code = '';
      this.step = 'email-password';
      this.notice = 'Password updated — sign in with your new password.';
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
    } finally {
      this.busy = false;
    }
  };

  // ─── Navigation between steps ────────────────────────────────────────
  // Reset transient flow state and show the requested step. Used by every
  // "Back to sign in" / "Create account" / "Forgot password?" link.
  private goToStep(step: Step): void {
    if (this.busy) return;
    this.error = null;
    this.notice = null;
    this.accountExists = false;
    this.code = '';
    // Sensitive fields never linger across a flow switch.
    if (step !== 'two-factor') this.password = '';
    if (step !== 'signup-complete') this.name = '';
    this.step = step;
  }

  private backToStart = (): void => this.goToStep('email-password');
  private goToSignup = (): void => this.goToStep('signup-email');
  private goToReset = (): void => this.goToStep('reset-email');

  // ─── Render ──────────────────────────────────────────────────────────
  render(): TemplateResult {
    if (isDevBypass()) return this.renderDevBypass();

    return html`
      <canvas class="ink" aria-hidden="true"></canvas>
      <div class="panel">
        <p class="eyebrow">Workplace by Ricoh</p>
        <h1>${this.heading()}</h1>
        <p class="subtitle">${this.subtitle()}</p>
        ${this.renderError()} ${this.renderNotice()} ${this.renderStep()}
      </div>
    `;
  }

  private heading(): string {
    switch (this.step) {
      case 'signup-email':
      case 'signup-complete':
        return 'Create account';
      case 'reset-email':
      case 'reset-confirm':
        return 'Reset password';
      default:
        return 'Sign in';
    }
  }

  private subtitle(): string {
    switch (this.step) {
      case 'signup-email':
      case 'signup-complete':
        return 'Set up access for your Ricoh account.';
      case 'reset-email':
      case 'reset-confirm':
        return 'We’ll email a code to reset your password.';
      default:
        return 'Access for verified Ricoh accounts.';
    }
  }

  private renderError(): TemplateResult {
    // aria-live region is ALWAYS present (even when empty) so screen readers
    // announce errors as they appear.
    return html`
      <div aria-live="polite" role="status">
        ${this.error ? html`<p class="error">${this.error}</p>` : ''}
      </div>
    `;
  }

  private renderNotice(): TemplateResult {
    // Neutral, calm status note (reset anti-enumeration, "password updated").
    // Its own always-present aria-live region, separate from the error one.
    return html`
      <div aria-live="polite" role="status">
        ${this.notice ? html`<p class="notice">${this.notice}</p>` : ''}
      </div>
    `;
  }

  private renderStep(): TemplateResult {
    switch (this.step) {
      case 'email-password':
        return this.renderEmailPassword();
      case 'two-factor':
        return this.renderTwoFactor();
      case 'signup-email':
        return this.renderSignupEmail();
      case 'signup-complete':
        return this.renderSignupComplete();
      case 'reset-email':
        return this.renderResetEmail();
      case 'reset-confirm':
        return this.renderResetConfirm();
      case 'done':
        return html`<p class="notice">Signing you in…</p>`;
    }
  }

  private renderEmailPassword(): TemplateResult {
    return html`
      <form @submit=${this.onSubmitEmailPassword} novalidate>
        <div class="field">
          <label for="email">Email address</label>
          <input
            id="email"
            data-autofocus
            type="email"
            name="email"
            autocomplete="email"
            inputmode="email"
            placeholder="you@ricoh-europe.com"
            .value=${this.email}
            ?disabled=${this.busy}
            required
            @input=${(e: Event) => (this.email = (e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label for="password">Password</label>
          <input
            id="password"
            type="password"
            name="password"
            autocomplete="current-password"
            placeholder="Your password"
            .value=${this.password}
            ?disabled=${this.busy}
            required
            @input=${(e: Event) => (this.password = (e.target as HTMLInputElement).value)}
          />
        </div>
        <button
          class="primary"
          type="submit"
          ?disabled=${this.busy || !this.email.trim() || !this.password}
        >
          ${this.busy ? 'Signing in…' : 'Sign in'}
        </button>
        <div class="links">
          <button class="link" type="button" ?disabled=${this.busy} @click=${this.goToSignup}>
            Create account
          </button>
          <button class="link" type="button" ?disabled=${this.busy} @click=${this.goToReset}>
            Forgot password?
          </button>
        </div>
      </form>
    `;
  }

  private renderTwoFactor(): TemplateResult {
    return html`
      <form @submit=${this.onSubmitCode} novalidate>
        <p class="notice">
          Enter the 6-digit code from your authenticator app to finish signing in.
        </p>
        <div class="field">
          <label for="code">Verification code</label>
          <input
            id="code"
            class="code"
            data-autofocus
            type="text"
            name="code"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            placeholder="000000"
            .value=${this.code}
            ?disabled=${this.busy}
            required
            @input=${(e: Event) => (this.code = (e.target as HTMLInputElement).value)}
          />
        </div>
        <button class="primary" type="submit" ?disabled=${this.busy || !this.code.trim()}>
          ${this.busy ? 'Verifying…' : 'Verify'}
        </button>
        <div class="links">
          <button class="link" type="button" ?disabled=${this.busy} @click=${this.backToStart}>
            Cancel
          </button>
        </div>
      </form>
    `;
  }

  private renderSignupEmail(): TemplateResult {
    return html`
      <form @submit=${this.onSubmitSignupEmail} novalidate>
        <p class="notice">
          Enter your Ricoh email and we’ll send you a code to verify it.
        </p>
        <div class="field">
          <label for="signup-email">Email address</label>
          <input
            id="signup-email"
            data-autofocus
            type="email"
            name="email"
            autocomplete="email"
            inputmode="email"
            placeholder="you@ricoh-europe.com"
            .value=${this.email}
            ?disabled=${this.busy}
            required
            @input=${(e: Event) => (this.email = (e.target as HTMLInputElement).value)}
          />
        </div>
        <button class="primary" type="submit" ?disabled=${this.busy || !this.email.trim()}>
          ${this.busy ? 'Sending code…' : 'Send code'}
        </button>
        <div class="links">
          ${this.accountExists
            ? html`<button
                class="link"
                type="button"
                ?disabled=${this.busy}
                @click=${this.backToStart}
              >
                Sign in instead
              </button>`
            : ''}
          <button class="link" type="button" ?disabled=${this.busy} @click=${this.backToStart}>
            Back to sign in
          </button>
        </div>
      </form>
    `;
  }

  private renderSignupComplete(): TemplateResult {
    return html`
      <form @submit=${this.onSubmitSignupComplete} novalidate>
        <p class="notice">
          Enter the 6-digit code we emailed to
          <span class="ident">${this.email.trim()}</span> and choose a password.
        </p>
        <div class="field">
          <label for="signup-code">Verification code</label>
          <input
            id="signup-code"
            class="code"
            data-autofocus
            type="text"
            name="code"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            placeholder="000000"
            .value=${this.code}
            ?disabled=${this.busy}
            required
            @input=${(e: Event) => (this.code = (e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label for="signup-password">Choose a password</label>
          <input
            id="signup-password"
            type="password"
            name="new-password"
            autocomplete="new-password"
            placeholder="Create a password"
            .value=${this.password}
            ?disabled=${this.busy}
            required
            @input=${(e: Event) => (this.password = (e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label for="signup-name">Your name <span style="font-weight:400">(optional)</span></label>
          <input
            id="signup-name"
            type="text"
            name="name"
            autocomplete="name"
            placeholder="Your name"
            .value=${this.name}
            ?disabled=${this.busy}
            @input=${(e: Event) => (this.name = (e.target as HTMLInputElement).value)}
          />
        </div>
        <button
          class="primary"
          type="submit"
          ?disabled=${this.busy || !this.code.trim() || !this.password}
        >
          ${this.busy ? 'Creating account…' : 'Create account'}
        </button>
        <div class="links">
          <button class="link" type="button" ?disabled=${this.busy} @click=${this.backToStart}>
            Back to sign in
          </button>
        </div>
      </form>
    `;
  }

  private renderResetEmail(): TemplateResult {
    return html`
      <form @submit=${this.onSubmitResetEmail} novalidate>
        <p class="notice">
          Enter your account email and we’ll send you a code to reset your password.
        </p>
        <div class="field">
          <label for="reset-email">Email address</label>
          <input
            id="reset-email"
            data-autofocus
            type="email"
            name="email"
            autocomplete="email"
            inputmode="email"
            placeholder="you@ricoh-europe.com"
            .value=${this.email}
            ?disabled=${this.busy}
            required
            @input=${(e: Event) => (this.email = (e.target as HTMLInputElement).value)}
          />
        </div>
        <button class="primary" type="submit" ?disabled=${this.busy || !this.email.trim()}>
          ${this.busy ? 'Sending code…' : 'Send reset code'}
        </button>
        <div class="links">
          <button class="link" type="button" ?disabled=${this.busy} @click=${this.backToStart}>
            Back to sign in
          </button>
        </div>
      </form>
    `;
  }

  private renderResetConfirm(): TemplateResult {
    return html`
      <form @submit=${this.onSubmitResetConfirm} novalidate>
        <div class="field">
          <label for="reset-code">Verification code</label>
          <input
            id="reset-code"
            class="code"
            data-autofocus
            type="text"
            name="code"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            placeholder="000000"
            .value=${this.code}
            ?disabled=${this.busy}
            required
            @input=${(e: Event) => (this.code = (e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label for="reset-password">New password</label>
          <input
            id="reset-password"
            type="password"
            name="new-password"
            autocomplete="new-password"
            placeholder="Create a new password"
            .value=${this.password}
            ?disabled=${this.busy}
            required
            @input=${(e: Event) => (this.password = (e.target as HTMLInputElement).value)}
          />
        </div>
        <button
          class="primary"
          type="submit"
          ?disabled=${this.busy || !this.code.trim() || !this.password}
        >
          ${this.busy ? 'Updating…' : 'Update password'}
        </button>
        <div class="links">
          <button class="link" type="button" ?disabled=${this.busy} @click=${this.backToStart}>
            Back to sign in
          </button>
        </div>
      </form>
    `;
  }

  private renderDevBypass(): TemplateResult {
    return html`
      <canvas class="ink" aria-hidden="true"></canvas>
      <div class="panel">
        <p class="eyebrow">Workplace by Ricoh</p>
        <h1>Sign in</h1>
        <div class="dev-banner">
          Dev-bypass mode active. Signed in as dev@ricoh-europe.com.
        </div>
        <button class="primary" type="button" @click=${() => navigate('/')}>
          Continue to deck-system
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'login-page': LoginPage;
  }
}
